import { MasonryProps } from '@anm/components/list/Masonry';
import { VirtualListProps } from '@anm/components/list/VirtualList';
import VirtualItem from '@anm/components/list/VirtualList/VirtualItem';
import makeValue from '@anm/helpers/makeValue';
import userRerenderOnResize from '@anm/hooks/userRerenderOnResize';
import isEqual from 'lodash/fp/isEqual';
import { memo, useEffect, FC } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { projectSelectors } from '../../../../../store/modules/project';
import { videoFolderActions, videoFolderSelectors } from '../../../../../store/modules/videoFolder';
import { createFolderAndProjectList } from '../../helpers';

import VirtualListWrapper from './Wrapper';

export type MasonryVideoFoldersProps = {
  canFocusField: boolean;
  onCanFocusField: (canFocus: boolean) => void;
  breakpointCols?: MasonryProps['breakpointCols'];
};
const MasonryVideoFolders: FC<MasonryVideoFoldersProps> = ({
  breakpointCols = {
    default: 5,
    1920: 4,
    1600: 3,
    1250: 2,
    769: 4,
    768: 3,
    767: 2
  },
  canFocusField,
  onCanFocusField
}) => {
  const dispatch = useDispatch();
  const folders = useSelector(videoFolderSelectors.selectCurrentFolders, shallowEqual);
  const projects = useSelector(videoFolderSelectors.selectCurrentProjects, shallowEqual);
  const searchKeyword = useSelector(projectSelectors.selectProjectsSearchKeyWord, shallowEqual);
  const searchProjects = useSelector(projectSelectors.selectSearchProjectsList, shallowEqual);

  userRerenderOnResize();

  useEffect(
    () => () => {
      dispatch(videoFolderActions.clearNewFolder());
    },
    []
  );

  if (!folders || !projects) return null;
  const list = searchKeyword
    ? createFolderAndProjectList([], searchProjects)
    : createFolderAndProjectList(folders, projects);

  return (
    <VirtualListWrapper<FC<VirtualListProps<typeof list[0]>>>
      itemPerPage={30}
      presetPagesCount={2}
      {...{ list, breakpointCols }}
      renderItem={({ id, height, renderItem }) => (
        <VirtualItem key={id} height={height + 133}>
          {renderItem({
            canFocusField,
            onCanFocusField
          })}
        </VirtualItem>
      )}
    />
  );
};

export const MasonryVideoFoldersMemorized = memo(
  MasonryVideoFolders,
  (p: MasonryVideoFoldersProps, n: MasonryVideoFoldersProps) =>
    p.canFocusField === n.canFocusField && isEqual(makeValue(p.breakpointCols), makeValue(n.breakpointCols))
);

export default MasonryVideoFolders;
